// feat: Zod schemas for input validation
import { z } from 'zod';
import { PROJECT_STATUSES } from '../types/project.js';

// ============================================================================
// Course Schemas
// ============================================================================

export const courseCreateSchema = z.object({
  title: z.string().min(1).max(200),
  description: z.string().min(1).max(2000),
  tags: z.array(z.string().min(1).max(50)).max(10).optional().default([]),
  published: z.boolean().optional().default(false),
});

export const courseUpdateSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  description: z.string().min(1).max(2000).optional(),
  tags: z.array(z.string().min(1).max(50)).max(10).optional(),
  published: z.boolean().optional(),
  expectedVersion: z.number().int().positive(),
});

// ============================================================================
// Lesson Schemas
// ============================================================================

export const lessonCreateSchema = z.object({
  title: z.string().min(1).max(200),
  content: z.string().optional(),
  durationMinutes: z.number().int().positive().optional(),
});

// ============================================================================
// Progress Schemas
// ============================================================================

export const progressUpdateSchema = z.object({
  currentPage: z.number().int().nonnegative().optional(),
  completedLessons: z.number().int().nonnegative().optional(),
  status: z.enum(['not-started', 'in-progress', 'completed', 'abandoned']).optional(),
  rating: z.number().int().min(1).max(5).optional(),
  totalPages: z.number().int().positive().optional(),
  totalLessons: z.number().int().positive().optional(),
});

// ============================================================================
// Canvas Schemas
// ============================================================================

export const canvasCreateSchema = z.object({
  title: z.string().min(1).max(200),
  tags: z.array(z.string().min(1).max(50)).max(10).optional().default([]),
});

export const canvasUpdateSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  tags: z.array(z.string().min(1).max(50)).max(10).optional(),
  expectedVersion: z.number().int().positive(),
});

const ALLOWED_CANVAS_TTL_MINUTES = [15, 30, 60, 90, 120] as const;
export const canvasShareSchema = z.object({
  ttlMinutes: z
    .number()
    .int()
    .refine((v) =>
      ALLOWED_CANVAS_TTL_MINUTES.includes(v as (typeof ALLOWED_CANVAS_TTL_MINUTES)[number]),
    )
    .optional()
    .default(60),
});

// ============================================================================
// Diagram Schemas
// ============================================================================

export const diagramCreateSchema = z.object({
  title: z.string().min(1).max(200),
  tags: z.array(z.string().min(1).max(50)).max(10).optional().default([]),
  kit: z.enum(['aws', 'uml', 'bpmn']).optional(),
  mxfile: z.string().max(10_000_000).optional(),
});

export const diagramUpdateSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  tags: z.array(z.string().min(1).max(50)).max(10).optional(),
  expectedVersion: z.number().int().positive(),
});

export const diagramThumbnailSchema = z.object({
  expectedVersion: z.number().int().positive(),
});

// ============================================================================
// Company Schemas (Freelance)
// ============================================================================

export const companyCreateSchema = z.object({
  name: z.string().min(1).max(120),
  website: z.url().max(300).optional(),
  notes: z.string().max(2000).optional(),
  tags: z.array(z.string().min(1).max(50)).max(10).optional().default([]),
});

export const companyUpdateSchema = z.object({
  expectedVersion: z.number().int().positive(),
  name: z.string().min(1).max(120).optional(),
  website: z.url().max(300).optional().nullable(),
  notes: z.string().max(2000).optional().nullable(),
  tags: z.array(z.string().min(1).max(50)).max(10).optional(),
});

// ============================================================================
// Project Schemas (Freelance)
// ============================================================================

export const projectCreateSchema = z.object({
  companyId: z.string().min(1),
  name: z.string().min(1).max(200),
  dueDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  status: z.enum(PROJECT_STATUSES).optional().default('draft'),
  tags: z.array(z.string().min(1).max(50)).max(10).optional().default([]),
});

export const projectUpdateSchema = z.object({
  expectedVersion: z.number().int().positive(),
  name: z.string().min(1).max(200).optional(),
  companyId: z.string().min(1).optional(),
  dueDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  status: z.enum(PROJECT_STATUSES).optional(),
  tags: z.array(z.string().min(1).max(50)).max(10).optional(),
});

export const projectFilePutSchema = z.object({
  expectedVersion: z.number().int().positive(),
  content: z.string().max(500_000),
});

export const projectQuestionCreateSchema = z.object({
  expectedVersion: z.number().int().positive(),
  title: z.string().min(1).max(1000),
  notes: z.string().max(10_000).optional(),
});

export const projectQuestionUpdateSchema = z.object({
  expectedVersion: z.number().int().positive(),
  status: z.enum(['open', 'clarified']).optional(),
  title: z.string().min(1).max(1000).optional(),
  notes: z.string().max(10_000).optional(),
});

export const projectVersionSchema = z.object({
  expectedVersion: z.number().int().positive(),
});

export const projectDiagramLinkSchema = z.object({
  expectedVersion: z.number().int().positive(),
  diagramId: z.string().min(1),
});

export const projectDiagramNewSchema = z.object({
  expectedVersion: z.number().int().positive(),
  title: z.string().min(1).max(200),
  kit: z.enum(['aws', 'uml', 'bpmn']).optional(),
});

export const projectAttachmentPresignSchema = z.object({
  filename: z.string().min(1),
  contentType: z.string().min(1),
});

export const projectAttachmentConfirmSchema = z.object({
  expectedVersion: z.number().int().positive(),
  filename: z.string().min(1),
  bytes: z.number().int().nonnegative(),
  contentType: z.string().min(1),
  note: z.string().max(2000).optional(),
});

// Capped list limits for unbounded project sub-lists (files/attachments/questions).
export const LIST_LIMIT_MAX = 500;
export const listLimitSchema = z.coerce.number().int().min(1).max(LIST_LIMIT_MAX).optional();

// ============================================================================
// Inferred Types
// ============================================================================

export type CourseCreateSchema = z.infer<typeof courseCreateSchema>;
export type CourseUpdateSchema = z.infer<typeof courseUpdateSchema>;
export type LessonCreateSchema = z.infer<typeof lessonCreateSchema>;
export type ProgressUpdateSchema = z.infer<typeof progressUpdateSchema>;
export type CanvasCreateSchema = z.infer<typeof canvasCreateSchema>;
export type CanvasUpdateSchema = z.infer<typeof canvasUpdateSchema>;
export type DiagramCreateSchema = z.infer<typeof diagramCreateSchema>;
export type DiagramUpdateSchema = z.infer<typeof diagramUpdateSchema>;
export type DiagramThumbnailSchema = z.infer<typeof diagramThumbnailSchema>;
export type CompanyCreateSchema = z.infer<typeof companyCreateSchema>;
export type CompanyUpdateSchema = z.infer<typeof companyUpdateSchema>;
export type ProjectCreateSchema = z.infer<typeof projectCreateSchema>;
export type ProjectUpdateSchema = z.infer<typeof projectUpdateSchema>;
export type ProjectFilePutSchema = z.infer<typeof projectFilePutSchema>;
export type ProjectQuestionCreateSchema = z.infer<typeof projectQuestionCreateSchema>;
export type ProjectQuestionUpdateSchema = z.infer<typeof projectQuestionUpdateSchema>;
export type ProjectDiagramLinkSchema = z.infer<typeof projectDiagramLinkSchema>;
export type ProjectVersionSchema = z.infer<typeof projectVersionSchema>;
export type ProjectDiagramNewSchema = z.infer<typeof projectDiagramNewSchema>;
export type ProjectAttachmentPresignSchema = z.infer<typeof projectAttachmentPresignSchema>;
export type ProjectAttachmentConfirmSchema = z.infer<typeof projectAttachmentConfirmSchema>;
export type ListLimitSchema = z.infer<typeof listLimitSchema>;
